import { useId } from 'react';
import type { ChangeEventHandler, FocusEventHandler, Ref } from 'react';
import styles from './text-field.module.css';

/**
 * `value` is required on purpose. React switches an input from uncontrolled to controlled the
 * moment it first receives a string after an undefined, which costs the caret its position and
 * writes a warning to the console - and that is exactly what an edit form does when it resets
 * itself once the record has loaded. Wire this up through Controller, not register().
 */
type TextFieldProps = {
  label: string;
  name: string;
  value: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  ref?: Ref<HTMLInputElement>;
  type?: 'text' | 'url' | 'password';
  inputMode?: 'text' | 'decimal' | 'numeric' | 'url';
  placeholder?: string;
  hint?: string;
  error?: string;
  disabled?: boolean;
  autoComplete?: string;
};

export const TextField = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  ref,
  type = 'text',
  inputMode,
  placeholder,
  hint,
  error,
  disabled = false,
  autoComplete = 'off',
}: TextFieldProps) => {
  const inputId = useId();
  const messageId = `${inputId}-message`;
  const hasError = error !== undefined;
  const message = error ?? hint;

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={inputId}>
        {label}
      </label>
      <input
        id={inputId}
        ref={ref}
        className={`${styles.input} ${hasError ? styles.invalid : ''}`}
        name={name}
        type={type}
        inputMode={inputMode}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        disabled={disabled}
        aria-invalid={hasError}
        aria-describedby={message === undefined ? undefined : messageId}
        onChange={onChange}
        onBlur={onBlur}
      />
      {message !== undefined && (
        <p
          id={messageId}
          className={hasError ? styles.error : styles.hint}
          role={hasError ? 'alert' : undefined}
        >
          {message}
        </p>
      )}
    </div>
  );
};
